import { Activity, AlertTriangle, ArrowLeft, Bed, Boxes, CheckCircle2, ClipboardCheck, FileSignature, IdCard, MessageCircle, Pill, Printer, Receipt, ShieldCheck, Stethoscope, Ticket, Workflow } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const SECTIONS = [
  {
    n: "01",
    title: "Front-of-house & OPD",
    icon: Stethoscope,
    items: [
      { id: 1, name: "The 11 a.m. queue cliff", what: "Walk-ins peak between 10–12; queues collapse, staff improvise.", cost: "10–18 min added wait per patient", fix: "Token Mgmt + OPD with predictive wait time", module: "Token + OPD" },
      { id: 2, name: "Repeat registration loops", what: "Same patient registered 2–3× because UHID search is slow.", cost: "Duplicate charts; wrong prescriptions", fix: "ABHA-linked dedup at the kiosk", module: "OPD" },
      { id: 3, name: "Doctor-room idle time", what: "Doctor waits for next patient because the queue isn't routed.", cost: "8–12% lost consult slots", fix: "Smart routing + token push", module: "Token Mgmt" },
    ],
  },
  {
    n: "02",
    title: "Wards, IPD & nursing",
    icon: Bed,
    items: [
      { id: 4, name: "Bed allocation by phone", what: "Admissions call ward to ask 'is bed 204 free?'", cost: "30–60 min admission delay", fix: "Live ward census board", module: "Ward + IPD" },
      { id: 5, name: "Paper vitals, lost vitals", what: "Vitals on paper miss the doctor round.", cost: "Re-work + safety risk", fix: "Nurse-station vitals capture", module: "Nurse Station" },
      { id: 6, name: "Handover gaps at shift change", what: "Verbal handovers miss critical alerts.", cost: "Repeat investigations, near-misses", fix: "Structured digital handover summary", module: "Nurse Station" },
      { id: 7, name: "Med-admin errors", what: "Wrong patient, wrong drug, wrong time.", cost: "Adverse events, complaints", fix: "Barcode-verified med admin", module: "Nurse Station" },
    ],
  },
  {
    n: "03",
    title: "Pharmacy & inventory",
    icon: Pill,
    items: [
      { id: 8, name: "Silent stockouts", what: "Drug 'available in stock' but actually out at the counter.", cost: "Lost revenue, lost trust", fix: "Token-linked dispensing with live stock", module: "Pharma Token" },
      { id: 9, name: "Expiry leakage", what: "Stock expires before dispensing.", cost: "1–3% of pharmacy revenue", fix: "Batch + expiry control with auto re-order", module: "Pharma Token" },
    ],
  },
  {
    n: "04",
    title: "Billing & revenue",
    icon: Receipt,
    items: [
      { id: 10, name: "Unbilled services", what: "Procedures performed but not posted to the bill.", cost: "2–6% revenue leakage", fix: "Order-to-bill auto-posting", module: "Billing" },
      { id: 11, name: "TPA / insurance delays", what: "Pre-auth & claim docs scattered across emails.", cost: "30–60 day DSO drag", fix: "TPA workflow with audit trail", module: "Billing" },
      { id: 12, name: "Tariff-mapping mistakes", what: "Wrong package code → claim rejection.", cost: "Rejections + rework", fix: "Tariff/package engine", module: "Billing" },
    ],
  },
  {
    n: "05",
    title: "Discharge & follow-up",
    icon: FileSignature,
    items: [
      { id: 13, name: "The 4-hour discharge", what: "Pending dues, missing summary, lab results trickling in.", cost: "Bed turnover loss + complaints", fix: "One-click summary + dues guardrail", module: "Discharge" },
      { id: 14, name: "Follow-ups that never happen", what: "Patient leaves; no reminder; never returns.", cost: "Lost LTV, bad outcomes", fix: "WhatsApp reminders + scheduled follow-up", module: "Discharge + WhatsApp" },
    ],
  },
  {
    n: "06",
    title: "Compliance & security",
    icon: ShieldCheck,
    items: [
      { id: 15, name: "ABDM gaps", what: "ABHA / HFR / HPR not linked → patient locked out of their own record.", cost: "Compliance + reputation", fix: "ABDM rails built-in", module: "Platform" },
      { id: 16, name: "Visitor chaos", what: "Visitors roam without ward limits or audit trail.", cost: "Security + infection risk", fix: "QR visitor passes with audit log", module: "Visitor Pass" },
      { id: 17, name: "Audit trail black holes", what: "Who edited what, when? Nobody knows.", cost: "Failed audits, regulatory pain", fix: "Full event log across modules", module: "Platform" },
    ],
  },
];

const MODULE_ICON = {
  "OPD": Stethoscope,
  "IPD": Bed,
  "Token Mgmt": Ticket,
  "Token + OPD": Ticket,
  "Ward + IPD": Boxes,
  "Nurse Station": Activity,
  "Pharma Token": Pill,
  "Billing": Receipt,
  "Discharge": FileSignature,
  "Discharge + WhatsApp": MessageCircle,
  "Visitor Pass": IdCard,
  "Platform": Workflow,
};

export default function AuditPage() {
  const onPrint = () => window.print();

  return (
    <main data-testid="audit-page" className="bg-ab-bg text-ab-text">
      <div className="print:hidden"><Navbar /></div>

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-12 print:pt-10 print:pb-6 overflow-hidden">
        <div className="absolute inset-0 -z-10 print:hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-ab-bg via-[#EFEDE6] to-ab-bg" />
          <div className="absolute -top-32 right-10 w-[420px] h-[420px] rounded-full bg-ab-accent/10 blur-3xl" />
        </div>

        <div className="ab-container">
          <div className="flex items-center justify-between gap-4 mb-10 print:mb-6">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-ab-muted hover:text-ab-primary transition print:hidden" data-testid="audit-back">
              <ArrowLeft className="w-4 h-4" /> Back to PranaOs
            </Link>
            <button onClick={onPrint} data-testid="audit-print-btn" className="ab-pill-btn-ghost print:hidden">
              <Printer className="w-4 h-4" /> Save as PDF
            </button>
          </div>

          <p className="ab-eyebrow text-ab-accent">The 5-minute audit</p>
          <h1 className="ab-h1 text-5xl md:text-7xl lg:text-8xl mt-5 max-w-5xl">
            17 places your hospital is <span className="italic font-light text-ab-accent">quietly bleeding</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-ab-muted leading-relaxed">
            A founder-curated checklist of where time, money and trust silently leak out of running a hospital — and the exact PranaOs module that closes each gap.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[
              { k: "Read time", v: "5 min" },
              { k: "Items", v: "17" },
              { k: "Categories", v: "6" },
              { k: "Audit type", v: "Self-serve" },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-ab-border rounded-2xl p-4">
                <p className="ab-eyebrow text-ab-muted">{s.k}</p>
                <p className="font-serif text-2xl text-ab-primary mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="ab-divider" />

      {/* HOW TO USE */}
      <section className="ab-container py-14 md:py-20 print:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4">
            <p className="ab-eyebrow text-ab-accent">How to use this</p>
            <h2 className="ab-h2 text-3xl md:text-4xl mt-3 text-ab-primary">5 minutes. <span className="italic font-light">Honest</span> answers.</h2>
          </div>
          <ol className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { n: "01", t: "Walk the floor", d: "Read each item with the people who actually run it — not from your office chair." },
              { n: "02", t: "Score 0–2", d: "0 = handled, 1 = patchy, 2 = bleeding. Be unkind. Future-you will thank you." },
              { n: "03", t: "Add the totals", d: "Anything > 12 means it's costing you a real number you'd rather not see." },
            ].map((s) => (
              <li key={s.n} className="bg-white border border-ab-border rounded-2xl p-6">
                <span className="font-mono-tech text-xs text-ab-accent tracking-widest">{s.n}</span>
                <h3 className="font-serif text-xl text-ab-primary mt-3">{s.t}</h3>
                <p className="mt-2 text-sm text-ab-muted leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* SECTIONS */}
      {SECTIONS.map((sec) => {
        const SecIcon = sec.icon;
        return (
          <section key={sec.n} className="ab-container py-14 md:py-20 print:py-6 break-inside-avoid">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <header className="lg:col-span-4 lg:sticky lg:top-28">
                <div className="flex items-center gap-3">
                  <span className="font-mono-tech text-xs text-ab-accent tracking-widest">{sec.n}</span>
                  <div className="flex-1 ab-shimmer-line h-px" />
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <span className="w-12 h-12 rounded-full bg-ab-primary text-white flex items-center justify-center"><SecIcon className="w-5 h-5" /></span>
                  <h2 className="font-serif text-3xl md:text-4xl text-ab-primary">{sec.title}</h2>
                </div>
              </header>

              <div className="lg:col-span-8 space-y-4">
                {sec.items.map((it) => {
                  const ModIcon = MODULE_ICON[it.module] || Workflow;
                  return (
                    <article
                      key={it.id}
                      data-testid={`audit-item-${it.id}`}
                      className="bg-white border border-ab-border rounded-3xl p-6 md:p-8 break-inside-avoid"
                    >
                      <div className="flex items-start gap-4">
                        <span className="font-mono-tech text-ab-muted text-sm pt-1 w-8 shrink-0">{String(it.id).padStart(2, "0")}</span>
                        <div className="flex-1">
                          <h3 className="font-serif text-2xl md:text-3xl text-ab-primary leading-tight">{it.name}</h3>
                          <p className="mt-2 text-ab-text/85">{it.what}</p>

                          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="rounded-xl bg-ab-bg border border-ab-border p-4">
                              <div className="flex items-center gap-2 ab-eyebrow text-ab-muted">
                                <AlertTriangle className="w-3.5 h-3.5" /> Hidden cost
                              </div>
                              <p className="mt-2 text-sm text-ab-text">{it.cost}</p>
                            </div>
                            <div className="rounded-xl bg-ab-primary text-white p-4">
                              <div className="flex items-center gap-2 ab-eyebrow text-white/60">
                                <CheckCircle2 className="w-3.5 h-3.5" /> What closes it
                              </div>
                              <p className="mt-2 text-sm">{it.fix}</p>
                            </div>
                          </div>

                          <div className="mt-5 flex items-center gap-3">
                            <span className="ab-eyebrow text-ab-muted">Module</span>
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ab-accent/10 text-ab-primary text-sm">
                              <ModIcon className="w-3.5 h-3.5" /> {it.module}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-2 text-xs text-ab-muted">
                              Score:
                              <span className="inline-flex gap-1">
                                <span className="w-4 h-4 rounded border border-ab-border" />
                                <span className="w-4 h-4 rounded border border-ab-border" />
                                <span className="w-4 h-4 rounded border border-ab-border" />
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {/* SCORECARD */}
      <section className="ab-container py-16 md:py-24 print:py-8 break-inside-avoid">
        <div className="bg-ab-primary text-white rounded-3xl p-10 md:p-16 relative overflow-hidden">
          <div className="absolute inset-0 ab-grain opacity-30 print:hidden" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="ab-eyebrow text-ab-accent">Your score</p>
              <h2 className="font-serif text-4xl md:text-6xl mt-4 leading-tight">
                Total it up. <span className="italic font-light">Honestly.</span>
              </h2>
              <ul className="mt-8 space-y-3 text-white/85 text-base leading-relaxed">
                <li><span className="font-mono-tech text-ab-accent mr-3">0–8</span> You're running a tight ship. The OS will mostly automate boring work.</li>
                <li><span className="font-mono-tech text-ab-accent mr-3">9–16</span> Real money is leaking. A 60-day rollout typically pays for itself in a quarter.</li>
                <li><span className="font-mono-tech text-ab-accent mr-3">17–24</span> Your team is heroic. They shouldn't have to be. Talk to us this week.</li>
                <li><span className="font-mono-tech text-ab-accent mr-3">25+</span> Every day you wait costs more than PranaOs for a year.</li>
              </ul>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-8">
                <ClipboardCheck className="w-8 h-8 text-ab-accent" />
                <p className="font-serif text-3xl mt-5 leading-snug">Send the score to a founder. We'll send a 30-min walk-through tailored to your top three leaks.</p>
                <Link
                  to="/#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.location.pathname !== "/") {
                      window.location.href = "/#contact";
                    } else {
                      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  data-testid="audit-cta-demo"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-ab-primary hover:bg-ab-accent hover:text-white transition print:hidden"
                >
                  Book a 30-min walk-through
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER NOTE / PRINT MARK */}
      <section className="ab-container pb-20 print:pb-4">
        <div className="ab-divider mb-6" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-ab-muted">
          <span>PranaOs · The hospital OS that breathes with you · pranaos.com</span>
          <span>© {new Date().getFullYear()} PranaOs Health Systems Pvt. Ltd. — Audit v1.0</span>
        </div>
      </section>

      <div className="print:hidden"><Footer /></div>
    </main>
  );
}
